import React from 'react';
import Reactor from 'data/reactor';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import DataLoader from 'data/loader';
import DataUtils from 'data/utils';
import { findShowMethodForModal } from 'utils/runtime';
import FormattingUtils from 'utils/formatting';
import StudentGetters from 'data/student/getters';
import RuntimeGetters from 'data/app-runtime/getters';
import RuntimeActions from 'data/app-runtime/actions';
import StudentActions from 'data/student/actions';
import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';

export default CreateReactClass({
    displayName: 'adjacent-student-button',
    mixins: [Reactor.ReactMixin],

    propTypes: {
        currentStudentBid: PropTypes.string,
        advancementDirection: PropTypes.oneOf(['previous', 'next']).isRequired,
        allowAdvanceStudent: PropTypes.bool,
        unsavedData: PropTypes.bool,
        modalReferences: PropTypes.object,
    },

    getDataBindings() {
        return {
            /* studentIsLoaded is used in a stately manner to re-render the directional buttons when available */
            studentIsLoaded: StudentGetters.studentByViewOrderIsLoaded(this.props.advancementDirection),
        };
    },

    getInitialState() {
        return {
            buttonClicked: false,
            buttonIsLoading: false,
        };
    },

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.currentStudentBid != this.props.currentStudentBid) {
            this.setState({ buttonClicked: false });
        }
    },

    shouldComponentUpdate(nextProps, nextState) {
        return (
            nextState.studentIsLoaded != this.state.studentIsLoaded ||
            nextProps.currentStudentBid != this.props.currentStudentBid ||
            nextProps.unsavedData != this.props.unsavedData ||
            nextProps.allowAdvanceStudent != this.props.allowAdvanceStudent
        );
    },

    getAdjacentStudent(referenceBid = null) {
        return Reactor.evaluate(
            this.props.advancementDirection == 'next'
                ? StudentGetters.nextStudent(referenceBid)
                : StudentGetters.previousStudent(referenceBid)
        );
    },

    advanceToStudentActions(adjacentStudentBid) {
        const newAdjacentStudent = this.getAdjacentStudent(adjacentStudentBid);
        const newAdjacentStudentBid = newAdjacentStudent ? newAdjacentStudent.get('studentBid', null) : null;

        // We are manually adding the 'slide-*' class in order to jump-start the transitions in the browser.
        // If we rely on React to invoke the transitions via the re-rendering process, there is a noticeable delay
        //     after clicking the button before the transition starts.
        // While this may be an anti-pattern, there is currently no known alternative that provides immediate browser
        //     response after the user action.
        const slideDirection = this.props.advancementDirection == 'next' ? 'left' : 'right';
        const studentSections = document.getElementsByClassName('student-info');
        for (let index = 0; index < studentSections.length; index++) {
            studentSections.item(index).className = studentSections.item(index).className.replace(' slide-left', '');
            studentSections.item(index).className = studentSections.item(index).className.replace(' slide-right', '');
            studentSections.item(index).className += ` slide-${slideDirection}`;
        }

        Reactor.batch(() => {
            RuntimeActions.allowAdvanceStudent(false);
            RuntimeActions.setAnimationIsRunning(true);
            RuntimeActions.setStudentTransition(slideDirection, 'changing-student');
        });

        DataLoader.loadStudentData(newAdjacentStudentBid, () => {}, 0);

        if (!Reactor.evaluate(StudentGetters.studentExistsInStore(newAdjacentStudentBid))) {
            // Update class and title for the clicked button
            const advancementButton = document.getElementById(this.props.advancementDirection + '-student-button');
            let classes = advancementButton.getAttribute('class').replace(' loading', '');
            advancementButton.setAttribute('class', `${classes} loading`);
            const loadingData = this.getLoadingData();
            advancementButton.setAttribute('title', loadingData.title);

            // Update class and title for other button to prevent clicking. This has shown to disrupt state when done with specific timing.
            const otherAdvancementDirection = this.props.advancementDirection == 'next' ? 'previous' : 'next';
            const otherAdvancementButton = document.getElementById(otherAdvancementDirection + '-student-button');
            classes = advancementButton.getAttribute('class').replace(' loading', '');
            otherAdvancementButton.setAttribute('class', `${classes} loading`);
        } // if (!Reactor.evaluate(StudentGetters.studentExistsInStore(adjacentStudentBid)))

        // Prevent repetitive clicking until requested student is loaded
        this.setState({ buttonClicked: true, buttonIsLoading: true });
    },

    advanceToStudent(adjacentStudentBid) {
        if (!this.state.buttonClicked || this.props.allowAdvanceStudent) {
            Analytics.recordButtonClickWithExpandedModuleAsLabel(
                CommonValues.SectionIDs.studentInfo,
                this.props.advancementDirection == 'next' ? 'next-student' : 'previous-student'
            );
            if (this.props.unsavedData) {
                const showSaveDataPrompt = findShowMethodForModal(
                    this.props.modalReferences,
                    CommonValues.sharedModalReferences.saveDataPrompt
                );
                showSaveDataPrompt()
                    .then(() => {
                        this.advanceToStudentActions(adjacentStudentBid);
                        RuntimeActions.setUnsavedData(false);
                    })
                    .catch(() => {
                        /* do nothing */
                    });
            } else {
                this.advanceToStudentActions(adjacentStudentBid);
            }
        }
    },

    getStudentData() {
        const adjacentStudent = this.getAdjacentStudent();
        const adjacentStudentBid = adjacentStudent ? adjacentStudent.get('studentBid', null) : null;
        const history = Reactor.evaluate(StudentGetters.studentInfo(adjacentStudentBid));
        const currentInfo = DataUtils.latestItemByTerm(history);
        const adjacentStudentName = currentInfo ? FormattingUtils.formatStudentName(currentInfo.toJS()) : '';
        const directionName =
            this.props.advancementDirection.substring(0, 1).toUpperCase() +
            this.props.advancementDirection.substring(1);
        const title = `${directionName} Student: ${adjacentStudentName}`;
        return { title, clickFunction: () => this.advanceToStudent(adjacentStudentBid) };
    },

    getLoadingData() {
        const directionName =
            this.props.advancementDirection.substring(0, 1).toUpperCase() +
            this.props.advancementDirection.substring(1);
        return { title: `Loading ${directionName} Student...`, clickFunction: () => {} };
    },

    getEmptyStudentPlaceholderData() {
        return {
            title: `There is no ${this.props.advancementDirection} student in this class`,
            clickFunction: () => {},
        };
    },

    render() {
        const id = `${this.props.advancementDirection}-student-button`;
        const adjacentStudent = this.getAdjacentStudent();
        const adjacentStudentBid =
            adjacentStudent && adjacentStudent.get('studentBid') ? adjacentStudent.get('studentBid') : null;
        const isAdjacentEmpty =
            adjacentStudent &&
            adjacentStudent.get('studentBid') === CommonValues.EMPTY_STUDENT_PLACEHOLDER.value.get('studentBid');
        let { title, clickFunction } = isAdjacentEmpty
            ? this.getEmptyStudentPlaceholderData()
            : this.state.studentIsLoaded
            ? this.getStudentData()
            : this.getLoadingData();

        let classes = 'move-to-student-button';
        classes += this.props.advancementDirection === 'previous' ? ' icon-chevron-left' : ' icon-chevron-right';

        if (!this.props.allowAdvanceStudent || isAdjacentEmpty) {
            clickFunction = () => {};
            classes += ' loading';
        }

        const linkTo =
            adjacentStudentBid && adjacentStudentBid !== CommonValues.EMPTY_STUDENT_PLACEHOLDER.value.get('studentBid')
                ? {
                      pathname: '/students/profile',
                      state: {
                          studentBid: adjacentStudentBid,
                      },
                  }
                : null;
        /**
         * Redacting student name used in 'title' attribute due to Heap collectino of pii data. See: https://nwea.tpondemand.com/entity/661254-sp-pii-date-collected-by-heap
         * March 2023 https://developers.heap.io/docs/web#precise-data-redaction-via-heap-redact
         */
        return (
            <Link
                data-heap-redact-attributes="title"
                id={id}
                className={classes}
                title={title}
                to={linkTo?.pathname}
                state={linkTo?.state}
                onClick={clickFunction}
            />
        );
    },
});
